import React, { useState } from 'react'
import { Skeleton, Empty, Radio, Badge} from 'antd'
import { PlusOutlined } from '@ant-design/icons'

import { Container } from "../../Styles"
import { StyledButton } from '../Button'

import { useCaseViewContext } from '../../context'
import { useCharting } from '../../hooks/useCharting'
import { useProviderNotes } from '../../hooks/useProviderNotes'
import { MODAL_MODES } from '../../constants'

import { ChartingEntryModal } from './Charting/ChartingEntryModal'
import { ProviderNoteModal } from './Charting/ProviderNoteModal'
import { ChartingEntry } from './Charting/ChartingEntry'
import { ProviderNote } from './Charting/ProviderNote'

export const Charting = () => {
    const { caseDetails, apiClasses, readOnlyMode, apiType, chartingPrescriptions } = useCaseViewContext()
    
    const [contentType, setContentType] = useState('charting')
    const [modalMode, setModalMode] = useState(null)
    const [selectedItem, setSelectedItem] = useState(null)

    const chartingHook = useCharting({ apiClasses, apiType })
    const providerNotesHook = useProviderNotes({ apiClasses, apiType })


    const onContentTypeChange = ({ target }) => {
        setContentType(target.value)
    }

    const onModalModeChange = (mode) => {
        setModalMode(mode)
    }

    const onSelect = (item) => {
        setSelectedItem(item)
        setModalMode(MODAL_MODES.EDIT)
    }

    const onCancel = () => {
        setSelectedItem(null)
        setModalMode(null)
        chartingHook.resetChartingEntry()
    }

    const renderChartingEntries = () => {
        const { chartingEntries, loading, deleteChartingEntry } = chartingHook

        if (loading) {
            return <Skeleton paragraph={{ rows: 3 }} />
        }

        if (!chartingEntries?.length) {
            return <Empty description="No charting entries available" />
        }

        return chartingEntries.map((entry) => (
            <ChartingEntry
                key={entry._id}
                item={entry}
                onSelect={onSelect}
                onDelete={deleteChartingEntry}
            />
        ))
    }

    const renderProviderNotes = () => {
        const { providerNotes, loading, deleteProviderNote } = providerNotesHook

        if (loading) {
            return <Skeleton paragraph={{ rows: 3 }} />
        }

        if (!providerNotes?.length) {
            return <Empty description="No provider notes available" />
        }

        return (
            <div>
                {providerNotes.map((note) => (
                    <ProviderNote
                        key={note._id}
                        item={note}
                        onSelect={onSelect}
                        onEdit={providerNotesHook.editProviderNote}
                        onDelete={deleteProviderNote}
                    />
                ))}
            </div>
        )
    }

    return (
        <Container>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '15px' 
            }}>
                <Radio.Group
                    onChange={onContentTypeChange}
                    value={contentType}
                >
                    <Radio.Button value={'charting'}>Charting</Radio.Button>
                    {apiType === 'admin' ? (
                        <Radio.Button value={'notes'}>
                            Notes
                        </Radio.Button>
                    ) : (
                        <Badge count={providerNotesHook.unreadNotesCount}>
                            <Radio.Button
                                button
                                value={'notes'}
                                onClick={providerNotesHook.clearUnreadNotesCount}
                            >
                                Notes
                            </Radio.Button>
                        </Badge>
                    )}
                </Radio.Group>
                {!readOnlyMode && (
                    <StyledButton
                        Icon={PlusOutlined}
                        borderColor="#7BAD7E"
                        textColor="#7BAD7E"
                        color="white"
                        tooltip='Add Note'
                        onClick={() => onModalModeChange(MODAL_MODES.ADD)}
                    />
                )}
            </div>

            <Skeleton loading={!caseDetails} active paragraph={{ rows: 6 }}>
                {contentType === 'charting' ? renderChartingEntries() : renderProviderNotes()}
            </Skeleton>

            {contentType === 'charting' && (
                <ChartingEntryModal
                    onCancel={onCancel}
                    editChartingEntry={chartingHook.editChartingEntry}
                    addChartingEntry={chartingHook.addChartingEntry}
                    selectedItem={selectedItem}
                    modalMode={modalMode}
                    loading={chartingHook.loading}
                    templates={chartingHook.templates}
                    prescriptions={chartingPrescriptions}
                />
            )}
            
            {contentType === 'notes' && (
                <ProviderNoteModal
                    onCancel={onCancel}
                    addProviderNote={providerNotesHook.addProviderNote}
                    editProviderNote={providerNotesHook.editProviderNote}
                    selectedItem={selectedItem}
                    modalMode={modalMode}
                    loading={providerNotesHook.loading}
                />
            )}
        </Container>
    )
}
